import type { CyclePeriod } from '../../contracts/cycles'
import { Tile } from '../components/Tile'
import { elementColor, palaceShort, sealColor } from '../colors'
import { formatDate, inDaysLabel, yearsLabel } from '../dates'
import { useNav } from '../nav'
import { haptic } from '../telegram'
import { useCycles } from './cyclesData'
import './cycles.css'

/**
 * Вкладка «Циклы» — «где ты сейчас»: четыре вложенных периода плитками,
 * ближайшие переходы и вход на жизненную шкалу. Плитка отвечает на вопрос
 * одним взглядом (что за период и сколько его прошло), разбор — на экране
 * периода: вкладка не должна превращаться в простыню.
 */
export function Cycles() {
  const nav = useNav()
  const { data, error } = useCycles()

  if (error) return <div className="state">{error}</div>
  if (!data) return <div className="state">Считаем, где ты сейчас…</div>

  const openPeriod = (period: CyclePeriod) => nav.navigate({ screen: { kind: 'cycle', period } })

  const { wave, month, year, decade } = data
  const decadeYears = decade.bazi
    ? `${decade.bazi.startYear}–${decade.bazi.endYear}`
    : decade.ziwei
      ? `${decade.ziwei.startYear}–${decade.ziwei.endYear}`
      : undefined
  const annual = year.ziwei?.annualLifePalace
  const yearNote = [year.tenGod.nameRu, annual ? `дворец ${palaceShort(annual.natalPalaceId, annual.natalPalaceRu)}` : null]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="screen cycles">
      <div className="topbar">
        <div className="brand">АТЛАС</div>
        <div className="topnote">циклы</div>
      </div>

      <h1 className="display">Где ты сейчас</h1>
      <div className="sub">
        {formatDate(data.date)} · {yearsLabel(data.age)}
      </div>

      <div className="tiles">
        <Tile
          label="Волна"
          title={`«${wave.seal.nameRu}» · ${wave.position}/13`}
          note={`${wave.today.tone.nameRu} тон`}
          progress={wave.position / 13}
          color={sealColor(wave.seal)}
          onClick={() => openPeriod('wave')}
        />
        <Tile
          label="Месяц"
          title={`${month.pillar.stemRu} ${month.pillar.branchRu}`}
          note={month.tenGod.nameRu}
          progress={month.progress}
          color={elementColor(month.pillar.element)}
          onClick={() => openPeriod('month')}
        />
        <Tile
          label="Год"
          title={`${year.pillar.stemRu} ${year.pillar.branchRu}`}
          note={yearNote}
          progress={year.progress}
          color={elementColor(year.pillar.element)}
          onClick={() => openPeriod('year')}
        />
        <Tile
          label="Десятилетие"
          title={decade.text.title}
          note={decadeYears}
          progress={decade.bazi?.progress}
          color={elementColor(decade.bazi?.pillar.element)}
          onClick={() => openPeriod('decade')}
        />
      </div>

      {data.transitions.length > 0 && (
        <div className="card transitions">
          <div className="lbl">Ближайшие переходы</div>
          {data.transitions.map((transition) => (
            <div className="transition" key={`${transition.kind}-${transition.date}`}>
              <div className="transition-head">
                <span className="transition-label">{transition.label}</span>
                <span className="transition-when">{inDaysLabel(transition.daysLeft)}</span>
              </div>
              <div className="transition-date">
                {transition.approximate ? '≈ ' : ''}
                {formatDate(transition.date, true)}
              </div>
              {transition.detail && <div className="transition-detail">{transition.detail}</div>}
            </div>
          ))}
        </div>
      )}

      <div className="rowlist cycles-rows">
        <button
          type="button"
          className="rowitem"
          onClick={() => {
            haptic('light')
            nav.navigate({ screen: { kind: 'lifescale' } })
          }}
        >
          <div>
            <div className="ttl">Жизненная шкала</div>
            <div className="note">Главы от рождения до девяноста</div>
          </div>
          <div className="right">›</div>
        </button>
      </div>

      {data.limitations.length > 0 && (
        <div className="quiet">
          {data.limitations.map((limitation) => (
            <p key={limitation.code}>{limitation.message}</p>
          ))}
        </div>
      )}
    </div>
  )
}
